import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Space,
  Select,
  Card,
  Avatar,
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Spin,
  Alert
} from "antd";
import {
  UserOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  DollarOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getServiceById, getExpertServices } from "../Services/ExpertService";
import { ExpertRecommendationLevelEnumLabels } from "Common/Constants/Enums/ExpertRecommendation";
import api from "Common/Client/Interceptor";

const { Title, Text } = Typography;
const { Option } = Select;

const EditExpertSupport = () => {
  const { courseId, serviceId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expertService, setExpertService] = useState(null);
  const [allExpertServices, setAllExpertServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [error, setError] = useState(null);

  // Fetch all expert services and current service if exists
  useEffect(() => {
    fetchAllExpertServices();
  }, []);

  // Fetch details when a service is selected
  useEffect(() => {
    if (selectedServiceId) {
      fetchExpertServiceDetails(selectedServiceId);
    }
  }, [selectedServiceId]);

  const fetchAllExpertServices = async () => {
    try {
      setLoading(true);
      const data = await getExpertServices();
      const services = data.items || [];
      setAllExpertServices(services);
      
      // If there's a real serviceId in the route (not the "new" sentinel), preselect it
      if (serviceId && serviceId !== 'new') {
        setSelectedServiceId(parseInt(serviceId));
      }
    } catch (err) {
      console.error("Error fetching expert services:", err);
      setError("Failed to load expert services");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpertServiceDetails = async (serviceId) => {
    try {
      setLoading(true);
      const serviceData = await getServiceById(serviceId);
      console.log("service data .................",serviceData);
      setExpertService(serviceData);
      
     // Set default form values
form.setFieldsValue({
  recommendationLevel: 0
});
    } catch (err) {
      console.error("Error fetching expert service details:", err);
      setError("Failed to load expert service details");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (serviceId) => {
    setSelectedServiceId(serviceId);
  };

 const handleSave = async (values) => {
  if (!selectedServiceId) {
    toast.error("Please select an expert service");
    return;
  }

  try {
    setSaving(true);
    const payload = {
      courseId: parseInt(courseId),
      expertServiceId: selectedServiceId,
      recommendationLevel: values.recommendationLevel
    };
    
    await api.post(`/indv/CourseExpertSupport/set`, payload);
    toast.success("Expert support updated successfully");
    navigate(`/proAdmin-dashboard/courseDetails/${courseId}`);
  } catch (err) {
    console.error("Error updating expert support:", err);
    toast.error(err.response?.data?.message || "Failed to update expert support");
  } finally {
    setSaving(false);
  }
};

  const handleBack = () => {
    navigate(`/proAdmin-dashboard/courseDetails/${courseId}`);
  };

  // Format duration from minutes to readable format
  const formatDuration = (minutes) => {
    if (!minutes) return "Not specified";
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  if (loading && !expertService) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleBack}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "24px" 
      }}>
        <Title level={2}>Edit Expert Support</Title>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
        >
          Back to Course
        </Button>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="expertServiceId"
            label="Select Expert Service"
            rules={[{ required: true, message: "Please select an expert service" }]}
          >
            <Select
              placeholder="Select an expert service"
              value={selectedServiceId}
              onChange={handleServiceChange}
              showSearch
              filterOption={(input, option) => {
                const service = allExpertServices.find(s => s.id === option?.value);
                if (!service) return false;
                return service.title.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {allExpertServices.map(service => (
                <Option key={service.id} value={service.id}>
                  <div>
                    <Text strong>{service.title}</Text>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {service.expert?.firstName} {service.expert?.lastName}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {expertService && (
            <>
              <Divider />
              
              {/* Expert Service Details */}
              <div style={{ marginBottom: "24px" }}>
                <Title level={4} style={{ marginBottom: "16px" }}>Expert Service Details</Title>
                <Row gutter={16}>
                  <Col span={6}>
                    <Avatar 
                      src={expertService.expert?.profileImage} 
                      icon={<UserOutlined />}
                      size={64}
                    />
                  </Col>
                  <Col span={18}>
                    <Title level={5} style={{ margin: 0 }}>
                      {expertService.title}
                    </Title>
                    <div style={{ marginBottom: "8px" }}>
                      <Text type="secondary">
                        Expert: {expertService.expert?.firstName} {expertService.expert?.lastName}
                      </Text>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <Text type="secondary">
                        {expertService.description}
                      </Text>
                    </div>
                    <Space>
                      <Tag color="gold">{expertService.expert?.expertCode}</Tag>
                      <Tag>{expertService.expert?.yearsOfExperience} years experience</Tag>
                      <Tag color={expertService.isActive ? "green" : "red"}>
                        {expertService.isActive ? "Active" : "Inactive"}
                      </Tag>
                    </Space>
                  </Col>
                </Row>
              </div>

              <Divider />


              <Form.Item
                name="recommendationLevel"
                label="Recommendation Level"
                rules={[{ required: true, message: "Please select a recommendation level" }]}
              >
                <Select placeholder="Select a recommendation level">
                  {Object.entries(ExpertRecommendationLevelEnumLabels).map(([key, label]) => (
                    <Option key={key} value={parseInt(key)}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saving}
                icon={<SaveOutlined />}
                disabled={!selectedServiceId}
              >
                Save Changes
              </Button>
              <Button onClick={handleBack}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default EditExpertSupport;